// ============================================================================
// GAME WORLD - Main Game State Container
// ============================================================================

import {
  EntityId,
  GamePhase,
  GameMode,
  GameState,
  ObjectiveState,
  PlayerInput,
  Team,
  HeroId,
  WeaponId,
  GameEventType,
} from '@/shared/types';
import { WEAPONS, GAMEPLAY, HEROES } from '@/shared/constants';
import { Vec2, randomRange } from '@/shared/utils';
import { CollisionWorld, CollisionLayers } from '@/engine/CollisionSystem';
import { Player } from './entities/Player';
import { Projectile } from './entities/Projectile';
import { Grenade } from './entities/Grenade';
import { Bomb } from './entities/Bomb';
import { Aircraft, AircraftType } from './entities/Aircraft';
import { SupplyCrate, SupplyType } from './entities/SupplyCrate';
import { GameMap, getMapById } from './map/GameMap';
import { BotControllerFactory, IBotController } from './ai/BotControllerFactory';
import { EffectSystem, EffectType, ZoneEffect } from './systems/EffectSystem';
import { AbilitySystem, VisualEffectType, Turret } from './systems/AbilitySystem';

export interface GameWorldConfig {
  mode: GameMode;
  maxPlayers: number;
  isHost: boolean;
  mapId?: string;  // Map to load (defaults to neon_arena)
  abilityQSlot?: 1 | 2;  // Which ability (1 or 2) is mapped to Q key input
  abilityFSlot?: 1 | 2;  // Which ability (1 or 2) is mapped to F key input
  totalRounds?: number;  // Best of X rounds (3, 5, 7, 9, 13)
}

export class GameWorld {
  // Config
  private config: GameWorldConfig;
  private isHost: boolean;
  private abilityQSlot: 1 | 2 = 1;  // Which ability index Q key triggers
  private abilityFSlot: 1 | 2 = 2;  // Which ability index F key triggers
  private totalRounds: number = 5;  // Best of X
  private roundsToWin: number = 3;  // First to X wins

  // State
  private phase: GamePhase = GamePhase.Lobby;
  private tick = 0;
  private time = 0;
  private roundNumber = 0;
  private roundStartTime = 0;
  private attackerScore = 0;
  private defenderScore = 0;

  // Entities
  private players = new Map<EntityId, Player>();
  private playersByPeerId = new Map<string, Player>();
  private projectiles = new Map<EntityId, Projectile>();
  private grenades = new Map<EntityId, Grenade>();
  private bombs = new Map<EntityId, Bomb>();
  private aircraft = new Map<EntityId, Aircraft>();
  private supplyCrates = new Map<EntityId, SupplyCrate>();

  // Systems
  private collisionWorld: CollisionWorld;
  private map: GameMap;

  // Objectives
  private objectives: ObjectiveState;

  // Events (for broadcasting)
  private pendingEvents: Array<{ type: GameEventType; data: unknown }> = [];

  // Recent shots for visual effects (hitscan tracers)
  private recentShots: Array<{ 
    start: { x: number; y: number }; 
    end: { x: number; y: number };
    shooterId: EntityId;
    shooterPeerId: string;
  }> = [];

  // Active ability effects for visualization
  private abilityEffects: Array<{
    type: 'shield' | 'shockwave' | 'dash' | 'cloak';
    playerId: EntityId;
    position: { x: number; y: number };
    direction: number;
    startTime: number;
    duration: number;
    radius?: number;
  }> = [];

  // Local player reference
  private localPlayerId: EntityId | null = null;

  // Bot AI controllers (uses IBotController for switchable AI modes)
  private bots = new Map<EntityId, IBotController>();

  // Round countdown system
  private countdownTime: number = 10000; // 10 seconds between rounds
  private countdownPaused: boolean = false;
  private countdownPausedBy: string = '';
  private countdownDuration: number = 10000; // Default countdown duration

  // Game pause state (tab focus sync)
  private gamePaused: boolean = false;
  private gamePausedBy: string = '';
  private pausedPlayerPeerIds: Set<string> = new Set();

  // Disconnect forfeit state
  private disconnectForfeit: boolean = false;
  private disconnectedPlayerName: string = '';

  // Effect and Ability Systems
  private effectSystem: EffectSystem;
  private abilitySystem: AbilitySystem;

  // Visual effect queue for rendering
  private visualEffectQueue: Array<{
    type: VisualEffectType;
    position: { x: number; y: number };
    direction?: number;
    radius?: number;
    duration: number;
    color?: string;
    targetPosition?: { x: number; y: number };
    startTime: number;
    followPlayerId?: string; // If set, effect follows this player's position/rotation
  }> = [];

  // Supply Drop System
  private aircraftSpawnedThisRound: number = 0;
  private aircraftPerRound: number = 5; // Calculated based on player count

  // Radio Call State (per player)
  private radioCallState = new Map<EntityId, {
    type: 'bomb' | 'missile' | null;
    firstClick: { x: number; y: number } | null;
    awaitingSecondClick: boolean;
  }>();

  constructor(config: GameWorldConfig) {
    this.config = config;
    this.isHost = config.isHost;
    this.abilityQSlot = config.abilityQSlot ?? 1;
    this.abilityFSlot = config.abilityFSlot ?? 2;
    this.totalRounds = config.totalRounds ?? 5;
    this.roundsToWin = Math.ceil(this.totalRounds / 2);  // e.g., 5 -> 3, 7 -> 4

    // Initialize systems
    this.collisionWorld = new CollisionWorld(128);
    
    // Load map by ID (fallback to neon_arena)
    this.map = getMapById(config.mapId || 'neon_arena');
    this.map.registerCollision(this.collisionWorld);

    // Initialize Effect System with callbacks
    this.effectSystem = new EffectSystem({
      onEffectApplied: (effect) => {
        console.log(`[EffectSystem] Applied ${effect.type} to ${effect.targetId}`);
      },
      onEffectRemoved: (effect) => {
        console.log(`[EffectSystem] Removed ${effect.type} from ${effect.targetId}`);
      },
      onEffectTick: (effect, value) => {
        // Handle DoT/HoT ticks
        const target = this.players.get(effect.targetId);
        if (!target || !target.alive) return;

        switch (effect.type) {
          case EffectType.Poison:
          case EffectType.Burn:
          case EffectType.Bleed:
            target.takeDamage(value, effect.sourceId, this.time);
            break;
          case EffectType.Regen:
            target.heal(value);
            break;
        }
      },
      onZoneTick: (zone, targetIds) => {
        // Handle zone effects
        for (const targetId of targetIds) {
          const target = this.players.get(targetId);
          if (!target || !target.alive) continue;

          // Check team filtering
          const source = this.players.get(zone.sourceId);
          if (!source) continue;

          const isAlly = target.team === source.team;
          const isEnemy = target.team !== source.team;

          if (zone.affectsAllies && isAlly) {
            this.applyZoneEffect(zone, target);
          }
          if (zone.affectsEnemies && isEnemy) {
            this.applyZoneEffect(zone, target);
          }
        }
      },
    });

    // Initialize Ability System
    this.abilitySystem = new AbilitySystem(this.effectSystem, {
      getPlayer: (id) => this.players.get(id),
      getPlayers: () => Array.from(this.players.values()),
      getEnemies: (team) => this.getPlayers().filter(p => p.team !== team && p.alive),
      getAllies: (team, excludeId) => this.getPlayers().filter(p => p.team === team && p.id !== excludeId),
      getTime: () => this.time,
      raycast: (start, direction, maxDist, _ignorePlayers) => {
        const hit = this.collisionWorld.raycast(start, direction, maxDist, CollisionLayers.WALL);
        return hit ? { point: hit.point, distance: Vec2.distance(start, hit.point) } : null;
      },
    });

    // Initialize objectives
    this.objectives = {
      spikeCarrierId: null,
      spikePlanted: false,
      spikeSite: -1,
      spikePlantTime: 0,
      spikeProgress: 0,
      spikePosition: null,
      plantingPlayerId: null,
      plantingPeerId: null,
      plantingProgress: 0,
      defusingPlayerId: null,
      defusingPeerId: null,
      defusingProgress: 0,
      reactorPosition: { x: 0, y: 0 },
      reactorProgress: 0,
      reactorContested: false,
      reactorPushers: 0,
    };
  }

  private applyZoneEffect(zone: ZoneEffect, target: Player): void {
    switch (zone.type) {
      case EffectType.Poison:
        target.takeDamage(zone.value, zone.sourceId, this.time, 'poison');
        break;
      case EffectType.Burn:
        // Pass 'fire' damage type for fire immunity check
        target.takeDamage(zone.value, zone.sourceId, this.time, 'fire');
        break;
      case EffectType.Regen:
        target.heal(zone.value);
        break;
      case EffectType.Slow:
        this.effectSystem.applyEffect(
          EffectType.Slow,
          zone.sourceId,
          target.id,
          500, // Short duration, refreshed by zone
          zone.value
        );
        break;
    }
  }

  // -------------------------------------------------------------------------
  // Player Management
  // -------------------------------------------------------------------------

  addPlayer(peerId: string, name: string, team: Team, heroId: HeroId): Player {
    // Check if player already exists with this peerId
    const existingPlayer = this.playersByPeerId.get(peerId);
    if (existingPlayer) {
      console.log(`[GameWorld] Player with peerId ${peerId} already exists, returning existing`);
      return existingPlayer;
    }
    
    const spawnIndex = this.getTeamPlayerCount(team);
    const spawnPos = this.map.getSpawnPoint(team, spawnIndex);

    const player = new Player(peerId, name, team, heroId, spawnPos);
    this.players.set(player.id, player);
    this.playersByPeerId.set(peerId, player);
    this.collisionWorld.addBody(player.getCollisionBody());
    
    console.log(`[GameWorld] Added player: ${name} (${peerId}) to team ${team}`);

    return player;
  }

  removePlayer(playerId: EntityId): void {
    const player = this.players.get(playerId);
    if (player) {
      this.collisionWorld.removeBody(playerId);
      this.playersByPeerId.delete(player.peerId);
      this.players.delete(playerId);
      this.bots.delete(playerId);
    }
  }

  getPlayer(playerId: EntityId): Player | undefined {
    return this.players.get(playerId);
  }

  getPlayerByPeerId(peerId: string): Player | undefined {
    return this.playersByPeerId.get(peerId);
  }
  
  getPlayerByName(name: string): Player | undefined {
    for (const player of this.players.values()) {
      if (player.name === name) {
        return player;
      }
    }
    return undefined;
  }
  
  // Update a player's peerId (used when network connects after player creation)
  updatePlayerPeerId(oldPeerId: string, newPeerId: string): void {
    const player = this.playersByPeerId.get(oldPeerId);
    if (player) {
      this.playersByPeerId.delete(oldPeerId);
      player.peerId = newPeerId;
      this.playersByPeerId.set(newPeerId, player);
      console.log(`[GameWorld] Updated player peerId: ${oldPeerId} -> ${newPeerId}`);
    }
  }

  getPlayers(): Player[] {
    return Array.from(this.players.values());
  }

  getTeamPlayers(team: Team): Player[] {
    return this.getPlayers().filter(p => p.team === team);
  }

  getTeamPlayerCount(team: Team): number {
    return this.getTeamPlayers(team).length;
  }

  setLocalPlayer(playerId: EntityId): void {
    this.localPlayerId = playerId;
  }

  getLocalPlayer(): Player | undefined {
    return this.localPlayerId ? this.players.get(this.localPlayerId) : undefined;
  }

  // -------------------------------------------------------------------------
  // Bot Management
  // -------------------------------------------------------------------------

  addBot(team: Team, heroId: HeroId = HeroId.Vanta, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): Player {
    const botIndex = this.bots.size;
    const player = this.addPlayer(
      `bot_${botIndex}`,
      `Bot ${botIndex + 1}`,
      team,
      heroId
    );
    
    // Configure AI based on difficulty
    const difficultySettings = this.getDifficultySettings(difficulty);
    
    // Create AI controller for this bot (using factory for switchable AI modes)
    const botController = BotControllerFactory.create(player, {
      aggressionLevel: difficultySettings.aggressionLevel + Math.random() * 0.1,
      accuracy: difficultySettings.accuracy + Math.random() * 0.1,
      reactionTime: difficultySettings.reactionTime + Math.random() * 50,
    });
    this.bots.set(player.id, botController);
    
    return player;
  }

  private getDifficultySettings(difficulty: 'easy' | 'medium' | 'hard'): { aggressionLevel: number; accuracy: number; reactionTime: number } {
    switch (difficulty) {
      case 'easy':
        return { aggressionLevel: 0.3, accuracy: 0.2, reactionTime: 400 };
      case 'medium':
        return { aggressionLevel: 0.5, accuracy: 0.5, reactionTime: 250 };
      case 'hard':
        return { aggressionLevel: 0.8, accuracy: 0.8, reactionTime: 100 };
      default:
        return { aggressionLevel: 0.5, accuracy: 0.5, reactionTime: 250 };
    }
  }

  spawnBots(count: number, team: Team, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): void {
    // Available weapons for bots (excluding pistol which they start with)
    const botWeapons = [WeaponId.NeoSMG9, WeaponId.PulseRifle, WeaponId.ScatterShot, WeaponId.RailSniper];
    
    for (let i = 0; i < count; i++) {
      const bot = this.addBot(team, HeroId.Vanta, difficulty);
      
      // Give each bot a random weapon
      const randomWeapon = botWeapons[Math.floor(Math.random() * botWeapons.length)]!;
      bot.buyWeapon(randomWeapon);
    }
  }

  // -------------------------------------------------------------------------
  // Game Flow
  // -------------------------------------------------------------------------

  startRound(): void {
    this.phase = GamePhase.RoundActive;
    this.roundNumber++;
    this.roundStartTime = this.time;

    // Reset players
    for (const player of this.players.values()) {
      const spawnIndex = this.getTeamPlayers(player.team).indexOf(player);
      const spawnPos = this.map.getSpawnPoint(player.team, spawnIndex);
      
      // Check if player was dead (collision body removed)
      const wasDead = !player.alive;
      
      player.respawn(spawnPos);
      player.resetAbilities(); // Reset all ability cooldowns for new round
      
      // Re-add collision body if it was removed on death
      if (wasDead) {
        this.collisionWorld.addBody(player.getCollisionBody());
      } else {
        this.collisionWorld.updateBody(player.id, player.position, player.velocity);
      }
    }

    // Clear projectiles
    this.projectiles.clear();

    // Clear supply drop system from previous round
    this.aircraft.clear();
    this.supplyCrates.clear();
    this.aircraftSpawnedThisRound = 0;

    // Calculate aircraft drops based on player count
    // Formula: player count + 2 (extra 2 are always radio calls)
    const totalPlayers = this.players.size;
    this.aircraftPerRound = totalPlayers + 2;
    
    // Build drop types array based on distribution
    const dropTypes: (SupplyType | 'random')[] = [];
    
    // Special case: 1v1 (2 players) = 2 weapons, 1 missile, 1 bomber
    if (totalPlayers === 2) {
      dropTypes.push(
        SupplyType.Weapon,
        SupplyType.Weapon,
        SupplyType.MissileStrike,
        SupplyType.BombCall
      );
    } else {
      // Always: 1 missile radio call, 1 bomber radio call
      dropTypes.push(SupplyType.MissileStrike);
      dropTypes.push(SupplyType.BombCall);
      
      // Remaining drops (totalPlayers drops to distribute)
      // Distribution: 57% weapon, 12% grenade, 11% health, 10% missile, 10% bomber
      const remaining = totalPlayers;
      const weaponCount = Math.round(remaining * 0.57);
      const grenadeCount = Math.round(remaining * 0.12);
      const healthCount = Math.round(remaining * 0.11);
      const missileCount = Math.round(remaining * 0.10);
      const bomberCount = Math.round(remaining * 0.10);
      
      // Add weapons
      for (let i = 0; i < weaponCount; i++) {
        dropTypes.push(SupplyType.Weapon);
      }
      // Add grenades
      for (let i = 0; i < grenadeCount; i++) {
        dropTypes.push(SupplyType.Grenade);
      }
      // Add health kits
      for (let i = 0; i < healthCount; i++) {
        dropTypes.push(SupplyType.HealthKit);
      }
      // Add extra missiles
      for (let i = 0; i < missileCount; i++) {
        dropTypes.push(SupplyType.MissileStrike);
      }
      // Add extra bombers
      for (let i = 0; i < bomberCount; i++) {
        dropTypes.push(SupplyType.BombCall);
      }
      
      // Adjust if we have too many or too few drops
      while (dropTypes.length < this.aircraftPerRound) {
        dropTypes.push(SupplyType.Weapon);
      }
      while (dropTypes.length > this.aircraftPerRound) {
        dropTypes.pop();
      }
    }
    
    // Shuffle the drop types for randomness (except always keep first two as guaranteed radio calls for non-1v1)
    if (totalPlayers > 2) {
      const guaranteedRadios = dropTypes.slice(0, 2);
      const rest = dropTypes.slice(2);
      for (let i = rest.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [rest[i], rest[j]] = [rest[j]!, rest[i]!];
      }
      dropTypes.splice(0, dropTypes.length, ...guaranteedRadios, ...rest);
    }
    
    console.log(`[Aircraft] Spawning ${this.aircraftPerRound} B-2 supply drops for ${totalPlayers} players`);

    for (let i = 0; i < this.aircraftPerRound; i++) {
      // Random spawn delay between 5-60 seconds
      const spawnDelay = 5000 + Math.random() * 55000;
      const dropType = dropTypes[i];
      
      setTimeout(() => {
        if (this.phase === GamePhase.RoundActive && this.isHost) {
          this.spawnAircraft(dropType);
        }
      }, spawnDelay);
    }

    // Clear ability effects and visual effects from previous round
    this.abilityEffects = [];
    this.visualEffectQueue = [];

    // Clear turrets from ability system
    this.abilitySystem.clearTurrets();

    // Clear all status effects from previous round
    this.effectSystem.clear();

    // Reset objectives
    this.objectives = {
      spikeCarrierId: null,
      spikePlanted: false,
      spikeSite: -1,
      spikePlantTime: 0,
      spikeProgress: 0,
      spikePosition: null,
      plantingPlayerId: null,
      plantingPeerId: null,
      plantingProgress: 0,
      defusingPlayerId: null,
      defusingPeerId: null,
      defusingProgress: 0,
      reactorPosition: { x: 0, y: 0 },
      reactorProgress: 0,
      reactorContested: false,
      reactorPushers: 0,
    };

    // Give spike to random attacker (for Data Spike mode)
    // Prefer human players over bots
    if (this.config.mode === GameMode.DataSpikeAssault) {
      const attackers = this.getTeamPlayers(Team.Attackers);
      // Filter to get only human players (not bots)
      const humanAttackers = attackers.filter(p => !this.bots.has(p.id));
      
      if (humanAttackers.length > 0) {
        // Give to random human player
        const carrier = humanAttackers[Math.floor(Math.random() * humanAttackers.length)]!;
        this.objectives.spikeCarrierId = carrier.id;
        console.log(`[Spike] Spike carrier assigned to human: ${carrier.name}`);
      } else if (attackers.length > 0) {
        // No human attackers, give to a bot as fallback
        const carrier = attackers[Math.floor(Math.random() * attackers.length)]!;
        this.objectives.spikeCarrierId = carrier.id;
        console.log(`[Spike] Spike carrier assigned to bot (no humans): ${carrier.name}`);
      }
    }
  }

  endRound(winningTeam: Team): void {
    console.log(`[GameWorld] endRound called - Winner: ${winningTeam === Team.Attackers ? 'Attackers' : 'Defenders'}`);
    
    this.phase = GamePhase.RoundEnd;
    
    // Reset round timer so display shows 3:00 during countdown
    this.roundStartTime = this.time;
    
    // Reset countdown for next round
    this.countdownTime = this.countdownDuration;
    this.countdownPaused = false;
    this.countdownPausedBy = '';

    if (winningTeam === Team.Attackers) {
      this.attackerScore++;
    } else if (winningTeam === Team.Defenders) {
      this.defenderScore++;
    }

    // Check match end - use configured roundsToWin
    if (this.attackerScore >= this.roundsToWin || this.defenderScore >= this.roundsToWin) {
      this.phase = GamePhase.MatchEnd;
      console.log(`[GameWorld] MATCH ENDED! Final Score: Attackers ${this.attackerScore} - Defenders ${this.defenderScore}`);
    } else {
      console.log(`[GameWorld] Round ended. Score: Attackers ${this.attackerScore} - Defenders ${this.defenderScore}. Countdown: ${this.countdownDuration/1000}s`);
    }
  }

  // End match due to player disconnect (forfeit)
  endMatchByDisconnect(disconnectedTeam: Team, disconnectedPlayerName: string): void {
    console.log(`[GameWorld] Player ${disconnectedPlayerName} disconnected - ${disconnectedTeam === Team.Attackers ? 'Attackers' : 'Defenders'} forfeit`);
    
    // Set winning score for the remaining team
    if (disconnectedTeam === Team.Attackers) {
      this.defenderScore = this.roundsToWin;
    } else {
      this.attackerScore = this.roundsToWin;
    }
    
    this.phase = GamePhase.MatchEnd;
    this.disconnectForfeit = true;
    this.disconnectedPlayerName = disconnectedPlayerName;
    
    console.log(`[GameWorld] MATCH ENDED BY DISCONNECT! ${disconnectedPlayerName} forfeited. Final Score: Attackers ${this.attackerScore} - Defenders ${this.defenderScore}`);
  }

  isDisconnectForfeit(): boolean {
    return this.disconnectForfeit;
  }

  getDisconnectedPlayerName(): string {
    return this.disconnectedPlayerName;
  }

  // Set disconnect forfeit state from network sync (for clients)
  setDisconnectForfeitState(isForfeit: boolean, playerName: string): void {
    this.disconnectForfeit = isForfeit;
    this.disconnectedPlayerName = playerName;
  }

  // Pause the countdown between rounds
  pauseCountdown(playerName: string): void {
    if (this.phase === GamePhase.RoundEnd && !this.countdownPaused) {
      this.countdownPaused = true;
      this.countdownPausedBy = playerName;
      console.log(`[GameWorld] Countdown paused by ${playerName} at ${(this.countdownTime/1000).toFixed(1)}s`);
    }
  }

  // Resume the countdown between rounds
  resumeCountdown(playerName: string): void {
    if (this.phase === GamePhase.RoundEnd && this.countdownPaused) {
      this.countdownPaused = false;
      this.countdownPausedBy = '';
      console.log(`[GameWorld] Countdown resumed by ${playerName} at ${(this.countdownTime/1000).toFixed(1)}s`);
    }
  }

  // Toggle pause state
  toggleCountdownPause(playerName: string): void {
    if (this.countdownPaused) {
      this.resumeCountdown(playerName);
    } else {
      this.pauseCountdown(playerName);
    }
  }

  // -------------------------------------------------------------------------
  // Game Pause (Tab Focus Sync)
  // -------------------------------------------------------------------------

  // Called when a player loses tab focus
  playerLostFocus(peerId: string, playerName: string): void {
    this.pausedPlayerPeerIds.add(peerId);
    if (!this.gamePaused) {
      this.gamePaused = true;
      this.gamePausedBy = playerName;
      console.log(`[GameWorld] Game paused - ${playerName} lost focus (${this.pausedPlayerPeerIds.size} players paused)`);
    }
  }

  // Called when a player regains tab focus
  playerRegainedFocus(peerId: string, playerName: string): void {
    this.pausedPlayerPeerIds.delete(peerId);
    console.log(`[GameWorld] ${playerName} regained focus (${this.pausedPlayerPeerIds.size} players still paused)`);
    
    // Resume only when all players are back
    if (this.pausedPlayerPeerIds.size === 0 && this.gamePaused) {
      this.gamePaused = false;
      this.gamePausedBy = '';
      console.log(`[GameWorld] Game resumed - all players have focus`);
    }
  }

  isGamePaused(): boolean {
    return this.gamePaused;
  }

  getGamePausedBy(): string {
    return this.gamePausedBy;
  }

  getPausedPlayerCount(): number {
    return this.pausedPlayerPeerIds.size;
  }

  // Set game pause state from network sync (for clients)
  setGamePauseState(isPaused: boolean, pausedBy: string): void {
    this.gamePaused = isPaused;
    this.gamePausedBy = pausedBy;
  }

  // -------------------------------------------------------------------------
  // Update Loop (Host Only)
  // -------------------------------------------------------------------------

  update(dt: number): void {
    if (!this.isHost) return;

    // Skip all updates when game is paused (tab focus)
    if (this.gamePaused) {
      return;
    }

    this.tick++;
    
    // Only increment game time during active round
    if (this.phase === GamePhase.RoundActive) {
      this.time += dt;
    }

    if (this.phase !== GamePhase.RoundActive) {
      // Handle round end -> countdown to next round
      if (this.phase === GamePhase.RoundEnd) {
        // Only countdown if not paused
        if (!this.countdownPaused) {
          this.countdownTime -= dt;
          
          if (this.countdownTime <= 0) {
            console.log(`[GameWorld] Countdown finished, starting next round`);
            this.startRound();
            return;
          }
        }
      }
      
      // Round not active - clear all player inputs and freeze positions
      for (const player of this.players.values()) {
        // Set zero input
        player.setInput({
          moveX: 0,
          moveY: 0,
          aimX: player.position.x,
          aimY: player.position.y,
          shoot: false,
          reload: false,
          ability1: false,
          ability2: false,
          ultimate: false,
          interact: false,
          throwGrenade: false,
          useBombCall: false,
          useMissileStrike: false,
          weaponSlot: 0,
          sequence: 0,
        });
        // Sync prevPosition to position to stop interpolation flickering
        player.prevPosition.x = player.position.x;
        player.prevPosition.y = player.position.y;
        // Zero out velocity
        player.velocity.x = 0;
        player.velocity.y = 0;
      }
      return;
    }

    // Update bot AI - generate inputs for bots
    const allPlayers = this.getPlayers();
    for (const [botId, botAI] of this.bots) {
      const bot = this.players.get(botId);
      if (bot && bot.alive) {
        const context = {
          dt,
          time: this.time,
          allPlayers,
          collisionWorld: this.collisionWorld,
          map: this.map,
          objectives: this.objectives,
        };
        const input = botAI.update(context);
        bot.setInput(input);
      }
    }

    // Update players
    for (const player of this.players.values()) {
      player.update(dt, this.time);
      this.collisionWorld.updateBody(player.id, player.position, player.velocity);
    }
    // Update projectiles
    for (const [id, projectile] of this.projectiles) {
      projectile.update(dt, this.time);
      if (!projectile.active) {
        this.projectiles.delete(id);
        this.collisionWorld.removeBody(id);
      } else {
        this.collisionWorld.updateBody(id, projectile.position, projectile.velocity);
      }
    }

    // Detect and resolve collisions (run multiple times to handle stacking and fast movement)
    for (let i = 0; i < 4; i++) {
      const collisions = this.collisionWorld.detectCollisions();
      this.collisionWorld.resolveCollisions();
      
      // Handle projectile collisions (triggers don't get resolved, so handle them here)
      if (i === 0) { // Only check on first iteration to avoid duplicate damage
        for (const collision of collisions) {
          const { bodyA, bodyB } = collision;
          
          // Check if either body is a projectile
          const projectileBody = bodyA.userData instanceof Projectile ? bodyA : 
                                 bodyB.userData instanceof Projectile ? bodyB : null;
          const otherBody = projectileBody === bodyA ? bodyB : bodyA;
          
          if (projectileBody && projectileBody.userData instanceof Projectile) {
            const projectile = projectileBody.userData;
            
            // Hit a player, enemy, or wall - destroy projectile
            if (otherBody.layer & (CollisionLayers.PLAYER | CollisionLayers.ENEMY | CollisionLayers.WALL)) {
              // Apply damage if hit a player (includes bots which are also Players)
              if (otherBody.userData instanceof Player) {
                const target = otherBody.userData;
                const shooter = this.players.get(projectile.ownerId);
                
                // Don't damage same team
                if (shooter && target.team !== shooter.team) {
                  target.takeDamage(projectile.damage, projectile.ownerId, this.time);
                  
                  // Give shooter ultimate charge (1 point per 25 damage)
                  const chargeGain = projectile.damage / 25;
                  shooter.ultimateCharge = Math.min(10, shooter.ultimateCharge + chargeGain);
                  
                  // Record damage event
                  this.pendingEvents.push({
                    type: GameEventType.PlayerDamage,
                    data: {
                      targetId: target.id,
                      attackerId: projectile.ownerId,
                      damage: projectile.damage,
                      position: projectile.position,
                      isHeadshot: false,
                    },
                  });
                  
                  // Check for kill
                  if (!target.alive) {
                    shooter.kills++;
                    shooter.credits += GAMEPLAY.KILL_REWARD;
                    shooter.ultimateCharge = Math.min(10, shooter.ultimateCharge + 2);
                    
                    this.collisionWorld.removeBody(target.id);
                    
                    this.pendingEvents.push({
                      type: GameEventType.PlayerKill,
                      data: {
                        victimId: target.id,
                        killerId: shooter.id,
                        weaponId: shooter.weapon.weaponId,
                        isHeadshot: false,
                      },
                    });
                  }
                }
              }
              
              // Destroy projectile on any collision
              projectile.destroy();
            }
          }
        }
      }
    }

    // Process shooting
    for (const player of this.players.values()) {
      if (player.input.shoot && player.canFire(this.time)) {
        this.processShot(player);
        player.fire(this.time);
      }
    }

    // Process grenade throwing
    for (const player of this.players.values()) {
      if (player.input.throwGrenade && player.grenadeCount > 0) {
        const cooldown = 1000; // 1 second between throws
        if (this.time - player.lastGrenadeThrowTime >= cooldown) {
          this.throwGrenade(player);
          player.grenadeCount--;
          player.lastGrenadeThrowTime = this.time;
        }
      }
    }

    // Process radio calls
    for (const player of this.players.values()) {
      // Bomb call radio (B-52 bomber - requires 2 clicks for path)
      if (player.input.useBombCall && player.bombCallCount > 0) {
        const state = this.radioCallState.get(player.id) || { type: null, firstClick: null, awaitingSecondClick: false };
        
        if (!state.awaitingSecondClick) {
          // First click - set start point
          state.type = 'bomb';
          state.firstClick = { x: player.input.aimX, y: player.input.aimY };
          state.awaitingSecondClick = true;
          this.radioCallState.set(player.id, state);
          console.log(`[Radio] ${player.name} activated bomb call - awaiting second click`);
        } else if (state.type === 'bomb' && state.firstClick) {
          // Second click - spawn bomber on path
          const endPoint = { x: player.input.aimX, y: player.input.aimY };
          this.spawnBomberOnPath(state.firstClick, endPoint);
          player.bombCallCount = 0;
          this.radioCallState.delete(player.id);
          console.log(`[Radio] ${player.name} called B-52 bomber strike`);
        }
      }

      // Missile strike radio (single click with laser targeting)
      if (player.input.useMissileStrike && player.missileStrikeCount > 0) {
        const state = this.radioCallState.get(player.id) || { type: null, firstClick: null, awaitingSecondClick: false };
        
        if (!state.awaitingSecondClick) {
          // First click - show laser targeting
          state.type = 'missile';
          state.firstClick = { x: player.input.aimX, y: player.input.aimY };
          state.awaitingSecondClick = true;
          this.radioCallState.set(player.id, state);
          console.log(`[Radio] ${player.name} targeting missile strike - awaiting confirmation`);
        } else if (state.type === 'missile') {
          // Second click - launch missiles
          const targetPos = { x: player.input.aimX, y: player.input.aimY };
          this.spawnMissileStrike(targetPos, player.id);
          player.missileStrikeCount--;
          this.radioCallState.delete(player.id);
          console.log(`[Radio] ${player.name} called missile strike at (${targetPos.x.toFixed(0)}, ${targetPos.y.toFixed(0)})`);
        }
      }
    }

    // Update grenades
    for (const [id, grenade] of this.grenades) {
      const exploded = grenade.update(dt, this.time, this.map.getWidth(), this.map.getHeight());
      if (exploded) {
        this.handleGrenadeExplosion(grenade);
        this.grenades.delete(id);
      } else if (!grenade.active) {
        this.grenades.delete(id);
      }
    }

    // Process abilities
    this.processAbilities();

    // Process spike planting/defusing (Data Spike mode)
    if (this.config.mode === GameMode.DataSpikeAssault) {
      this.processSpikeInteraction(dt);
    }

    // Process supply drops
    this.updateSupplyDropSystem(dt);

    // Check round end conditions
    this.checkRoundEnd();

    // Clamp players to map boundaries (safety net)
    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const tileSize = this.map.getTileSize();
    const playerRadius = 16;
    const minBound = tileSize + playerRadius;
    const maxBoundX = mapWidth - tileSize - playerRadius;
    const maxBoundY = mapHeight - tileSize - playerRadius;

    for (const player of this.players.values()) {
      // Clamp position to map bounds
      player.position.x = Math.max(minBound, Math.min(maxBoundX, player.position.x));
      player.position.y = Math.max(minBound, Math.min(maxBoundY, player.position.y));
      
      // Update collision body position (keep reference intact)
      this.collisionWorld.updateBody(player.id, player.position, player.velocity);
    }
  }

  // -------------------------------------------------------------------------
  // Client-Side Prediction (for local player only on clients)
  // -------------------------------------------------------------------------

  updateLocalPlayerOnly(dt: number): void {
    const localPlayer = this.getLocalPlayer();
    
    if (this.phase !== GamePhase.RoundActive) return;

    // Update local player's movement and weapon (if alive)
    if (localPlayer && localPlayer.alive) {
      localPlayer.update(dt, this.time);
      
      // Update collision body
      this.collisionWorld.updateBody(localPlayer.id, localPlayer.position, localPlayer.velocity);
      
      // Run collision detection for local player against walls
      // This prevents walking through walls on client-side prediction
      this.collisionWorld.detectCollisions();
      this.collisionWorld.resolveCollisions();
      
      // Clamp to map boundaries
      const mapWidth = this.map.getWidth();
      const mapHeight = this.map.getHeight();
      const tileSize = this.map.getTileSize();
      const playerRadius = 16;
      const minBound = tileSize + playerRadius;
      const maxBoundX = mapWidth - tileSize - playerRadius;
      const maxBoundY = mapHeight - tileSize - playerRadius;
      
      localPlayer.position.x = Math.max(minBound, Math.min(maxBoundX, localPlayer.position.x));
      localPlayer.position.y = Math.max(minBound, Math.min(maxBoundY, localPlayer.position.y));
    }
    
    // Smoothly interpolate remote players towards their target positions
    // This makes remote player movement look smooth even with 20Hz updates
    const interpSpeed = 15; // Higher = faster snap to target
    for (const player of this.players.values()) {
      if (player.id === this.localPlayerId) continue;
      
      if (player.targetPosition) {
        player.prevPosition = { ...player.position };
        // Lerp towards target position
        const t = 1 - Math.exp(-interpSpeed * dt / 1000);
        player.position.x += (player.targetPosition.x - player.position.x) * t;
        player.position.y += (player.targetPosition.y - player.position.y) * t;
      }
      if (player.targetRotation !== null) {
        player.prevRotation = player.rotation;
        // Lerp towards target rotation
        const t = 1 - Math.exp(-interpSpeed * dt / 1000);
        player.rotation += (player.targetRotation - player.rotation) * t;
      }
    }
    
    // Also update projectiles visually (interpolate between state updates)
    // This makes bullets look smooth even if state only comes 20 times/sec
    for (const projectile of this.projectiles.values()) {
      projectile.update(dt, this.time);
    }
  }

  private processShot(player: Player): void {
    const weapon = WEAPONS[player.weapon.weaponId];
    
    // Calculate shot direction with spread
    const baseAngle = player.rotation;
    const spread = weapon.spread;
    const pellets = weapon.id === WeaponId.ScatterShot ? weapon.burstCount : 1;

    for (let i = 0; i < pellets; i++) {
      const angle = baseAngle + randomRange(-spread, spread);
      const direction = Vec2.fromAngle(angle);
      const start = Vec2.add(player.position, Vec2.mul(direction, 20)); // Start slightly in front

      // Hitscan
      if (weapon.projectileSpeed === 0) {
        const hit = this.collisionWorld.raycast(
          start,
          direction,
          weapon.range,
          CollisionLayers.PLAYER | CollisionLayers.ENEMY | CollisionLayers.WALL
        );

        // Calculate end point for tracer visualization
        const endPoint = hit 
          ? hit.point 
          : Vec2.add(start, Vec2.mul(direction, weapon.range));
        
        // Record shot for visual tracer
        this.recentShots.push({
          start: { x: start.x, y: start.y },
          end: { x: endPoint.x, y: endPoint.y },
          shooterId: player.id,
          shooterPeerId: player.peerId,
        });

        if (hit && hit.body.userData instanceof Player) {
          const target = hit.body.userData;
          if (target.id !== player.id && target.team !== player.team) {
            // Calculate if headshot (top 30% of hitbox)
            const isHeadshot = Math.random() < GAMEPLAY.HEADSHOT_ZONE_RATIO;
            // Use player's effective damage (includes passive bonus)
            const baseDamage = player.getEffectiveDamage();
            const damage = isHeadshot 
              ? baseDamage * weapon.headshotMultiplier 
              : baseDamage;

            target.takeDamage(damage, player.id, this.time);
            
            // Gain ultimate charge from damage (1 point per 25 damage)
            const chargeGain = damage / 25;
            player.ultimateCharge = Math.min(10, player.ultimateCharge + chargeGain);

            // Record damage event
            this.pendingEvents.push({
              type: GameEventType.PlayerDamage,
              data: {
                targetId: target.id,
                attackerId: player.id,
                damage,
                position: hit.point,
                isHeadshot,
              },
            });

            // Check for kill
            if (!target.alive) {
              player.kills++;
              player.credits += GAMEPLAY.KILL_REWARD;
              player.ultimateCharge = Math.min(10, player.ultimateCharge + 2); // Bonus charge for kill

              // Remove dead player's collision body so bullets pass through
              this.collisionWorld.removeBody(target.id);

              this.pendingEvents.push({
                type: GameEventType.PlayerKill,
                data: {
                  victimId: target.id,
                  killerId: player.id,
                  weaponId: player.weapon.weaponId,
                  isHeadshot,
                },
              });
            }
          }
        }
      } else {
        // Projectile-based weapon
        const projectile = new Projectile(
          player.id,
          start,
          direction,
          weapon.projectileSpeed,
          weapon.damage,
          2000, // 2 second lifetime
          this.time
        );
        this.projectiles.set(projectile.id, projectile);
        this.collisionWorld.addBody(projectile.getCollisionBody());
      }
    }
  }

  private throwGrenade(player: Player): void {
    // Calculate throw direction (towards aim point)
    const direction = Vec2.normalize({
      x: player.input.aimX - player.position.x,
      y: player.input.aimY - player.position.y,
    });

    // Spawn grenade slightly in front of player
    const spawnOffset = 30;
    const spawnPos = {
      x: player.position.x + direction.x * spawnOffset,
      y: player.position.y + direction.y * spawnOffset,
    };

    // Throw power
    const throwPower = 0.5; // pixels per ms

    const grenade = new Grenade(player.id, spawnPos, direction, throwPower);
    this.grenades.set(grenade.id, grenade);

    // Emit event for sound/visual
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'grenade_throw',
        playerId: player.id,
        position: spawnPos,
      },
    });

    console.log(`[Grenade] ${player.name} threw grenade (${player.grenadeCount} remaining)`);
  }

  private handleGrenadeExplosion(grenade: Grenade): void {
    const explosionRadius = 150; // pixels
    const baseDamage = 80;

    // Find all players in explosion radius
    for (const player of this.players.values()) {
      if (!player.alive) continue;

      const dist = Vec2.distance(player.position, grenade.position);
      if (dist <= explosionRadius) {
        // Damage falls off with distance
        const damageFalloff = 1 - (dist / explosionRadius);
        const damage = Math.floor(baseDamage * damageFalloff);

        if (damage > 0) {
          player.takeDamage(damage, grenade.ownerId, this.time);

          // Record damage event
          this.pendingEvents.push({
            type: GameEventType.PlayerDamage,
            data: {
              targetId: player.id,
              attackerId: grenade.ownerId,
              damage,
              position: grenade.position,
              isHeadshot: false,
            },
          });

          // Check for kill
          if (!player.alive) {
            const attacker = this.players.get(grenade.ownerId);
            if (attacker) {
              attacker.kills++;
              attacker.credits += GAMEPLAY.KILL_REWARD;
              attacker.ultimateCharge = Math.min(10, attacker.ultimateCharge + 2);
            }

            this.collisionWorld.removeBody(player.id);

            this.pendingEvents.push({
              type: GameEventType.PlayerKill,
              data: {
                victimId: player.id,
                killerId: grenade.ownerId,
                weaponId: 'grenade',
                isHeadshot: false,
              },
            });
          }
        }
      }
    }

    // Emit explosion event for visual/audio
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'grenade_explosion',
        position: grenade.position,
        radius: explosionRadius,
      },
    });

    console.log(`[Grenade] Explosion at (${grenade.position.x.toFixed(0)}, ${grenade.position.y.toFixed(0)})`);
  }

  private processAbilities(): void {
    // Clean up expired ability effects (legacy)
    this.abilityEffects = this.abilityEffects.filter(
      effect => this.time < effect.startTime + effect.duration
    );

    // Clean up expired visual effects
    this.visualEffectQueue = this.visualEffectQueue.filter(
      effect => this.time < effect.startTime + effect.duration
    );

    // Update effect system
    this.effectSystem.update(this.time, (pos, radius) => {
      const nearby: EntityId[] = [];
      for (const player of this.players.values()) {
        if (Vec2.distance(player.position, pos) <= radius) {
          nearby.push(player.id);
        }
      }
      return nearby;
    });

    // Update turrets
    const turretShots = this.abilitySystem.updateTurrets(16); // Assuming ~60fps
    for (const shot of turretShots) {
      const target = this.players.get(shot.targetId);
      if (target && target.alive) {
        target.takeDamage(shot.damage, shot.turretId as EntityId, this.time);
      }
    }

    // Process player ability inputs
    for (const player of this.players.values()) {
      if (!player.alive) continue;

      // Check if this is the local player (use ability slot mapping) or a bot (use default 1/2)
      const isLocalPlayer = player.id === this.localPlayerId;
      const qAbilityIndex = isLocalPlayer ? this.abilityQSlot : 1;
      const fAbilityIndex = isLocalPlayer ? this.abilityFSlot : 2;

      // Q key - mapped ability
      if (player.input.ability1 && player.canUseAbility(qAbilityIndex, this.time)) {
        console.log(`[Input] Q pressed (ability1) -> executing ability index ${qAbilityIndex}`);
        this.executeAbilityNew(player, qAbilityIndex);
      }

      // F key - mapped ability  
      if (player.input.ability2 && player.canUseAbility(fAbilityIndex, this.time)) {
        console.log(`[Input] F pressed (ability2) -> executing ability index ${fAbilityIndex}`);
        this.executeAbilityNew(player, fAbilityIndex);
      }

      // Ultimate (X key)
      if (player.input.ultimate) {
        const canUse = player.canUseAbility(3, this.time);
        const heroDef = HEROES[player.heroId];
        const ultCost = heroDef?.abilities[3]?.ultimateCost || 6;
        console.log(`[Input] X pressed - ultimate charge: ${player.ultimateCharge.toFixed(1)}/${ultCost}, canUse: ${canUse}`);
        if (canUse) {
          this.executeAbilityNew(player, 3);
        }
      }

      // Apply effect modifiers to player
      this.applyEffectModifiers(player);
    }
  }

  private executeAbilityNew(player: Player, abilityIndex: number): void {
    const heroDef = HEROES[player.heroId];
    const abilityDef = heroDef?.abilities[abilityIndex];
    console.log(`[Ability] Executing ability ${abilityIndex}: ${abilityDef?.name || 'Unknown'} for ${heroDef?.name || 'Unknown hero'}`);
    
    player.useAbility(abilityIndex, this.time);

    // Execute ability through the new system
    const result = this.abilitySystem.executeAbility(player, abilityIndex);
    
    if (!result.success) return;

    // Handle teleportation
    if (result.teleportTo) {
      player.position = { ...result.teleportTo };
      player.prevPosition = { ...result.teleportTo };
      this.collisionWorld.updateBody(player.id, player.position);
    }

    // Handle damage to targets
    if (result.damage && result.targetIds) {
      for (const targetId of result.targetIds) {
        const target = this.players.get(targetId);
        if (target && target.alive) {
          target.takeDamage(result.damage, player.id, this.time);
          
          if (!target.alive) {
            player.kills++;
            player.credits += GAMEPLAY.KILL_REWARD;
            this.collisionWorld.removeBody(target.id);
          }
        }
      }
    }

    // Handle healing
    if (result.healing && result.targetIds) {
      for (const targetId of result.targetIds) {
        const target = this.players.get(targetId);
        if (target) {
          // Revive if dead
          if (!target.alive) {
            target.alive = true;
            target.health = result.healing;
            this.collisionWorld.addBody(target.getCollisionBody());
          } else {
            target.heal(result.healing);
          }
        }
      }
    }

    // Queue visual effects
    if (result.visualEffects) {
      for (const vfx of result.visualEffects) {
        this.visualEffectQueue.push({
          ...vfx,
          startTime: this.time,
        });
      }
    }

    // Add legacy ability effects for backward compatibility
    if (result.effectsApplied.includes('cloak')) {
      this.abilityEffects.push({
        type: 'cloak',
        playerId: player.id,
        position: { ...player.position },
        direction: player.rotation,
        startTime: this.time,
        duration: 4000,
      });
      player.isInvisible = true;
    }
  }

  private applyEffectModifiers(player: Player): void {
    // Apply speed modifier from effects
    const speedMod = this.effectSystem.getSpeedModifier(player.id);
    player.speedMultiplier = speedMod;

    // Check stun status
    player.isStunned = this.effectSystem.isStunned(player.id);

    // Check cloak status
    const isCloaked = this.effectSystem.isCloaked(player.id);
    player.isInvisible = isCloaked;

    // Apply damage reduction when taking damage (handled in takeDamage)
    // Fire rate modifier (handled in weapon system)
  }

  // Calculate damage with effect modifiers
  calculateDamage(baseDamage: number, attackerId: EntityId, targetId: EntityId): number {
    let damage = baseDamage;

    // Apply attacker's damage boost
    const damageBoost = this.effectSystem.getDamageModifier(attackerId);
    damage *= damageBoost;

    // Check if target is marked (bonus damage)
    const markedEffect = this.effectSystem.getEffect(targetId, EffectType.Marked);
    if (markedEffect) {
      damage *= 1 + markedEffect.value / 100;
    }

    // Apply target's damage reduction
    const reduction = this.effectSystem.getDamageReduction(targetId);
    damage *= 1 - reduction / 100;

    return Math.floor(damage);
  }

  // Get fire rate modifier for a player
  getFireRateModifier(playerId: EntityId): number {
    return this.effectSystem.getFireRateModifier(playerId);
  }

  // Get all zone effects for rendering
  getZoneEffects(): ZoneEffect[] {
    return this.effectSystem.getZones();
  }

  // Get turrets for rendering
  getTurrets(): Turret[] {
    return this.abilitySystem.getTurrets();
  }

  // Get visual effects for rendering
  getVisualEffects(): typeof this.visualEffectQueue {
    return [...this.visualEffectQueue];
  }

  getAbilityEffects(): Array<{
    type: 'shield' | 'shockwave' | 'dash' | 'cloak';
    playerId: EntityId;
    position: { x: number; y: number };
    direction: number;
    startTime: number;
    duration: number;
    radius?: number;
  }> {
    return [...this.abilityEffects];
  }

  // Effect system accessors
  getEffectSystem(): EffectSystem {
    return this.effectSystem;
  }

  hasEffect(playerId: EntityId, type: EffectType): boolean {
    return this.effectSystem.hasEffect(playerId, type);
  }

  // -------------------------------------------------------------------------
  // Spike Interaction (Data Spike Assault Mode)
  // -------------------------------------------------------------------------

  private processSpikeInteraction(dt: number): void {
    const plantTime = GAMEPLAY.SPIKE_PLANT_TIME;
    const defuseTime = GAMEPLAY.SPIKE_DEFUSE_TIME;
    
    // If spike is planted, handle defusing
    if (this.objectives.spikePlanted) {
      this.handleSpikeDefusing(dt, defuseTime);
    } else {
      // Spike not planted yet - handle planting
      this.handleSpikePlanting(dt, plantTime);
    }
  }

  private handleSpikePlanting(dt: number, plantTime: number): void {
    const carrier = this.objectives.spikeCarrierId ? this.players.get(this.objectives.spikeCarrierId) : null;
    
    // Check if carrier is alive and pressing interact
    if (!carrier || !carrier.alive) {
      // Carrier dead - transfer spike to another attacker if possible
      // Prefer human players over bots
      if (this.objectives.spikeCarrierId) {
        const aliveAttackers = this.getTeamPlayers(Team.Attackers).filter(p => p.alive && p.id !== this.objectives.spikeCarrierId);
        const aliveHumans = aliveAttackers.filter(p => !this.bots.has(p.id));
        
        if (aliveHumans.length > 0) {
          const newCarrier = aliveHumans[0]!;
          this.objectives.spikeCarrierId = newCarrier.id;
          console.log(`[Spike] Spike transferred to human: ${newCarrier.name}`);
        } else if (aliveAttackers.length > 0) {
          const newCarrier = aliveAttackers[0]!;
          this.objectives.spikeCarrierId = newCarrier.id;
          console.log(`[Spike] Spike transferred to bot: ${newCarrier.name}`);
        }
      }
      this.objectives.plantingPlayerId = null;
      this.objectives.plantingPeerId = null;
      this.objectives.plantingProgress = 0;
      return;
    }

    // Check if carrier is in a spike site
    const siteIndex = this.map.isInSpikeSite(carrier.position);
    if (siteIndex === -1) {
      // Not in a spike site
      if (this.objectives.plantingPlayerId) {
        console.log(`[Spike] Planting cancelled - left spike site`);
      }
      this.objectives.plantingPlayerId = null;
      this.objectives.plantingPeerId = null;
      this.objectives.plantingProgress = 0;
      return;
    }

    // Check if pressing interact (E key)
    if (carrier.input.interact) {
      // Start or continue planting
      if (this.objectives.plantingPlayerId !== carrier.id) {
        this.objectives.plantingPlayerId = carrier.id;
        this.objectives.plantingPeerId = carrier.peerId;
        this.objectives.plantingProgress = 0;
        console.log(`[Spike] Starting to plant at site ${siteIndex === 0 ? 'A' : 'B'}`);
      }
      
      // Increment progress
      this.objectives.plantingProgress += dt / plantTime;
      
      // Check if planting complete
      if (this.objectives.plantingProgress >= 1) {
        this.objectives.spikePlanted = true;
        this.objectives.spikeSite = siteIndex;
        this.objectives.spikePlantTime = this.time;
        this.objectives.spikePosition = { ...carrier.position };
        this.objectives.plantingPlayerId = null;
        this.objectives.plantingPeerId = null;
        this.objectives.plantingProgress = 0;
        console.log(`[Spike] ★ SPIKE PLANTED at site ${siteIndex === 0 ? 'A' : 'B'}! Defenders have ${GAMEPLAY.SPIKE_UPLOAD_TIME / 1000}s to defuse!`);
      }
    } else {
      // Released interact - cancel planting
      if (this.objectives.plantingPlayerId) {
        console.log(`[Spike] Planting cancelled - released E key`);
        this.objectives.plantingPlayerId = null;
        this.objectives.plantingPeerId = null;
        this.objectives.plantingProgress = 0;
      }
    }
  }

  private handleSpikeDefusing(dt: number, defuseTime: number): void {
    // Find defender trying to defuse
    let defusingPlayer: Player | null = null;
    
    // Debug: Log all defenders' interact state (only when spike is planted)
    if (this.tick % 20 === 0) { // Log every ~1 second to avoid spam
      for (const player of this.players.values()) {
        if (player.team === Team.Defenders && player.alive) {
          const dist = this.objectives.spikePosition 
            ? Vec2.distance(player.position, this.objectives.spikePosition) 
            : 999;
          console.log(`[Spike] Defender ${player.name} (${player.peerId}): interact=${player.input?.interact}, dist=${dist.toFixed(0)}, isBot=${this.bots.has(player.id)}`);
        }
      }
    }

    for (const player of this.players.values()) {
      if (!player.alive || player.team !== Team.Defenders) continue;
      
      // Safety check: ensure player.input exists
      if (!player.input) {
        console.warn(`[Spike] Player ${player.name} has no input object!`);
        continue;
      }
      
      if (!player.input.interact) continue;
      
      // Check if close enough to spike (within 50 pixels)
      if (!this.objectives.spikePosition) continue;
      const dist = Vec2.distance(player.position, this.objectives.spikePosition);
      if (dist <= 50) {
        defusingPlayer = player;
        console.log(`[Spike] Found defusing player: ${player.name} (interact=${player.input.interact}, dist=${dist.toFixed(0)})`);
        break;
      }
    }

    if (defusingPlayer) {
      // Start or continue defusing
      if (this.objectives.defusingPlayerId !== defusingPlayer.id) {
        this.objectives.defusingPlayerId = defusingPlayer.id;
        this.objectives.defusingPeerId = defusingPlayer.peerId;
        this.objectives.defusingProgress = 0;
        console.log(`[Spike] ${defusingPlayer.name} started defusing!`);
      }
      
      // Increment progress
      this.objectives.defusingProgress += dt / defuseTime;
      
      // Check if defuse complete
      if (this.objectives.defusingProgress >= 1) {
        console.log(`[Spike] ★ SPIKE DEFUSED by ${defusingPlayer.name}! Defenders win!`);
        this.endRound(Team.Defenders);
      }
    } else {
      // No one defusing - reset progress
      if (this.objectives.defusingPlayerId) {
        const oldDefuser = this.players.get(this.objectives.defusingPlayerId);
        console.log(`[Spike] Defuse cancelled by ${oldDefuser?.name || 'unknown'}`);
      }
      this.objectives.defusingPlayerId = null;
      this.objectives.defusingPeerId = null;
      this.objectives.defusingProgress = 0;
    }
  }

  // -------------------------------------------------------------------------
  // Supply Drop System
  // -------------------------------------------------------------------------

  private updateSupplyDropSystem(dt: number): void {
    // Update aircraft
    for (const [id, aircraft] of this.aircraft) {
      aircraft.update(dt, this.time);
      
      if (aircraft.type === AircraftType.SupplyDrop) {
        // Supply drop aircraft - single crate drop
        if (!aircraft.hasDropped && aircraft.active) {
          const flightProgress = Vec2.distance(aircraft.prevPosition, aircraft.targetPosition) / 
                                Vec2.distance(aircraft.position, aircraft.targetPosition);
          
          if (!aircraft.dropTime && flightProgress > 0.3 && Math.random() < 0.01) {
            aircraft.dropTime = this.time;
            aircraft.hasDropped = true;
            
            const direction = aircraft.getRotation();
            const dropOffset = 80;
            const dropPosition = {
              x: aircraft.position.x - Math.cos(direction) * dropOffset,
              y: aircraft.position.y - Math.sin(direction) * dropOffset,
            };
            
            // Use forced drop type if specified
            const forcedType = (aircraft as any).forcedDropType;
            this.spawnSupplyCrate(dropPosition, forcedType);
          }
        }
      } else if (aircraft.type === AircraftType.Bomber) {
        // B-52 Bomber - carpet bombing (series of bombs)
        const totalBombs = 8; // Number of bombs to drop
        const bombInterval = 200; // Distance between bombs (pixels)
        const flightDistance = Vec2.distance(aircraft.prevPosition, aircraft.position);
        
        // Drop bombs at regular intervals along flight path
        if (aircraft.bombsDropped < totalBombs && flightDistance > 0) {
          const totalFlightDistance = Vec2.distance(aircraft.startPosition, aircraft.targetPosition);
          const currentProgress = Vec2.distance(aircraft.startPosition, aircraft.position);
          
          // Start dropping after 20% of flight path
          if (currentProgress > totalFlightDistance * 0.2) {
            const expectedBombs = Math.floor((currentProgress - totalFlightDistance * 0.2) / bombInterval);
            
            if (expectedBombs > aircraft.bombsDropped) {
              this.dropBomb(aircraft);
              aircraft.bombsDropped++;
            }
          }
        }
      }
      
      // Remove inactive aircraft
      if (!aircraft.active) {
        this.aircraft.delete(id);
      }
    }
    
    // Update bombs
    for (const [id, bomb] of this.bombs) {
      const exploded = bomb.update(dt, this.time);
      
      if (exploded) {
        // Play explosion sound for missiles
        if (bomb.launchPosition && bomb.targetPosition) {
          this.pendingEvents.push({
            type: GameEventType.RoundStarted,
            data: {
              eventType: 'missile_explosion',
              position: bomb.position,
            },
          });
        }
        this.handleBombExplosion(bomb);
        this.bombs.delete(id);
      } else if (!bomb.active) {
        this.bombs.delete(id);
      }
    }
    
    // Update supply crates
    for (const [id, crate] of this.supplyCrates) {
      const justLanded = crate.update(dt, this.time);
      
      // Emit landing event for sound
      if (justLanded) {
        this.pendingEvents.push({
          type: GameEventType.RoundStarted,
          data: {
            eventType: 'parachute_landed',
            crateId: crate.id,
          },
        });
      }
      
      // Check for player pickups (only when landed)
      if (crate.isLanded() && !crate.claimed) {
        for (const player of this.players.values()) {
          if (!player.alive) continue;
          
          const dist = Vec2.distance(player.position, crate.position);
          if (dist <= 40) { // Pickup radius
            this.handleCratePickup(player, crate);
            break;
          }
        }
      }
      
      // Remove claimed or old crates (60 seconds timeout)
      if (crate.claimed || (crate.landTime && this.time - crate.landTime > 60000)) {
        this.supplyCrates.delete(id);
        this.collisionWorld.removeBody(id);
      }
    }
  }

  private spawnAircraft(dropType?: SupplyType | 'random'): void {
    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const margin = 200; // Start/end outside visible area
    
    // Random edge: 0=top, 1=right, 2=bottom, 3=left
    const startEdge = Math.floor(Math.random() * 4);
    const endEdge = (startEdge + 2) % 4; // Opposite edge
    
    let startPos: { x: number; y: number };
    let endPos: { x: number; y: number };
    
    // Determine start and end positions
    switch (startEdge) {
      case 0: // Top
        startPos = { x: Math.random() * mapWidth, y: -margin };
        break;
      case 1: // Right
        startPos = { x: mapWidth + margin, y: Math.random() * mapHeight };
        break;
      case 2: // Bottom
        startPos = { x: Math.random() * mapWidth, y: mapHeight + margin };
        break;
      default: // Left
        startPos = { x: -margin, y: Math.random() * mapHeight };
        break;
    }
    
    switch (endEdge) {
      case 0: // Top
        endPos = { x: Math.random() * mapWidth, y: -margin };
        break;
      case 1: // Right
        endPos = { x: mapWidth + margin, y: Math.random() * mapHeight };
        break;
      case 2: // Bottom
        endPos = { x: Math.random() * mapWidth, y: mapHeight + margin };
        break;
      default: // Left
        endPos = { x: -margin, y: Math.random() * mapHeight };
        break;
    }
    
    // Only spawn supply drop aircraft (B-2 Spirit) automatically
    // Bombers (B-52) are now player-controlled via radio calls
    const aircraftType = AircraftType.SupplyDrop;
    const speed = 150;
    const altitude = 400; // Increased from 200
    
    const aircraft = new Aircraft(startPos, endPos, speed, altitude, this.time, aircraftType);
    // Store the desired drop type on the aircraft
    (aircraft as any).forcedDropType = dropType;
    this.aircraft.set(aircraft.id, aircraft);
    this.aircraftSpawnedThisRound++;
    
    // Emit event for aircraft spawn sound
    this.pendingEvents.push({
      type: GameEventType.RoundStarted, // Reuse event type
      data: {
        eventType: 'aircraft_spawn',
        aircraftId: aircraft.id,
        aircraftType: aircraftType,
      },
    });
    
    const typeStr = 'SupplyDrop'; // Only supply drops spawn automatically now
    console.log(`[Aircraft] ${typeStr} ${this.aircraftSpawnedThisRound}/${this.aircraftPerRound} spawned, flying from edge ${startEdge} to ${endEdge}`);
  }

  private spawnBomberOnPath(startPoint: { x: number; y: number }, endPoint: { x: number; y: number }): void {
    // Extend path beyond map edges for proper entry/exit
    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const margin = 200;
    
    // Calculate direction vector
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length < 100) {
      console.log('[Radio] Bomber path too short, ignoring');
      return;
    }
    
    // Normalize and extend
    const ndx = dx / length;
    const ndy = dy / length;
    
    // Extend start backwards and end forwards
    const extendedStart = {
      x: Math.max(-margin, Math.min(mapWidth + margin, startPoint.x - ndx * 300)),
      y: Math.max(-margin, Math.min(mapHeight + margin, startPoint.y - ndy * 300)),
    };
    
    const extendedEnd = {
      x: Math.max(-margin, Math.min(mapWidth + margin, endPoint.x + ndx * 300)),
      y: Math.max(-margin, Math.min(mapHeight + margin, endPoint.y + ndy * 300)),
    };
    
    // Spawn B-52 bomber at high altitude
    const bomber = new Aircraft(extendedStart, extendedEnd, 100, 1200, this.time, AircraftType.Bomber);
    this.aircraft.set(bomber.id, bomber);
    
    // Emit event
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'aircraft_spawn',
        aircraftId: bomber.id,
        aircraftType: AircraftType.Bomber,
      },
    });
    
    console.log(`[Radio] B-52 Bomber spawned on custom path`);
  }

  private spawnMissileStrike(targetPos: { x: number; y: number }, callerId: EntityId): void {
    // Launch 5-7 missiles from a consistent off-screen direction
    const missileCount = 5 + Math.floor(Math.random() * 3); // 5-7 missiles
    const strikeRadius = 200; // Spread of impact points
    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    
    // Launch from top-left corner (north-west), just outside map bounds
    // This ensures missiles are visible as they fly across the map
    const margin = 300; // Distance outside map edge
    const baseLaunchX = -margin;
    const baseLaunchY = -margin;
    
    for (let i = 0; i < missileCount; i++) {
      // Stagger missiles slightly in formation (small spread at launch)
      const formationSpread = 150;
      const formationOffset = (i - missileCount / 2) * (formationSpread / missileCount);
      
      const launchPosition = {
        x: baseLaunchX + formationOffset,
        y: baseLaunchY + formationOffset * 0.5, // Slight Y variation
      };
      
      // Each missile targets a point within the strike radius
      const targetAngle = Math.random() * Math.PI * 2;
      const targetDistance = Math.random() * strikeRadius;
      const targetPosition = {
        x: targetPos.x + Math.cos(targetAngle) * targetDistance,
        y: targetPos.y + Math.sin(targetAngle) * targetDistance,
      };
      
      // Clamp target position to map bounds
      targetPosition.x = Math.max(50, Math.min(mapWidth - 50, targetPosition.x));
      targetPosition.y = Math.max(50, Math.min(mapHeight - 50, targetPosition.y));
      
      // Create missile with trajectory data
      const bomb = new Bomb(
        callerId,
        { ...launchPosition },
        { x: 0, y: 0 },
        0 // Altitude will be calculated by arc trajectory
      );
      
      // Set missile-specific data with staggered launch and arrival times
      // Each missile launches 150ms after the previous one and arrives 150ms later as well
      bomb.launchPosition = launchPosition;
      bomb.targetPosition = targetPosition;
      bomb.launchTime = this.time + i * 150; // Stagger launch by 150ms each
      bomb.flightDuration = 7000; // Each missile takes 7 seconds from launch to impact
      
      console.log(`[Missile] Created missile ${i+1}/${missileCount}: launch=(${launchPosition.x.toFixed(0)},${launchPosition.y.toFixed(0)}) target=(${targetPosition.x.toFixed(0)},${targetPosition.y.toFixed(0)}) launchTime=${bomb.launchTime.toFixed(0)} arrivalTime=${(bomb.launchTime + bomb.flightDuration).toFixed(0)}`);
      
      this.bombs.set(bomb.id, bomb);
      this.collisionWorld.addBody(bomb.getCollisionBody());
    }
    
    // Play rocket launch sound immediately for the entire strike
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'rocket_launch',
        position: targetPos,
      },
    });
    
    // Schedule incoming warning sound to play after 5 seconds (2 seconds before 7 second impact)
    setTimeout(() => {
      this.pendingEvents.push({
        type: GameEventType.RoundStarted,
        data: {
          eventType: 'missile_incoming',
          position: targetPos,
        },
      });
    }, 5000);
    
    // Emit event
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'missile_strike',
        position: targetPos,
        radius: strikeRadius,
      },
    });
    
    console.log(`[Radio] Missile strike spawned ${missileCount} missiles at (${targetPos.x.toFixed(0)}, ${targetPos.y.toFixed(0)})`);
  }

  private spawnSupplyCrate(position: { x: number; y: number }, forcedType?: SupplyType | 'random'): void {
    // Get map bounds
    const mapWidth = this.map.getWidth();
    const mapHeight = this.map.getHeight();
    const margin = 100; // Stay away from edges
    
    // Clamp initial position within map bounds
    let dropPosition = {
      x: Math.max(margin, Math.min(mapWidth - margin, position.x)),
      y: Math.max(margin, Math.min(mapHeight - margin, position.y)),
    };
    
    // Find valid ground position (not on walls)
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      // Check if position is valid (not in a wall and within bounds)
      const tileX = Math.floor(dropPosition.x / this.map.getTileSize());
      const tileY = Math.floor(dropPosition.y / this.map.getTileSize());
      const tiles = this.map.data.tiles;
      
      if (tiles[tileY] && tiles[tileY]![tileX] !== 1) {
        break; // Valid position found (not a wall)
      }
      
      // Try random nearby position (but keep within bounds)
      dropPosition = {
        x: Math.max(margin, Math.min(mapWidth - margin, position.x + (Math.random() - 0.5) * 200)),
        y: Math.max(margin, Math.min(mapHeight - margin, position.y + (Math.random() - 0.5) * 200)),
      };
      attempts++;
    }
    
    // Determine supply type
    let supplyType: SupplyType;
    let weaponId: WeaponId | undefined;
    let amount: number | undefined;
    
    // Use forced type if specified
    if (forcedType === 'random') {
      // Random radio call (bomb or missile)
      supplyType = Math.random() < 0.5 ? SupplyType.BombCall : SupplyType.MissileStrike;
      amount = 1;
    } else if (forcedType) {
      supplyType = forcedType;
      if (supplyType === SupplyType.Grenade) {
        amount = 1;
      } else if (supplyType === SupplyType.Weapon) {
        // Pick random weapon
        const weaponOptions = [
          WeaponId.NeoSMG9,
          WeaponId.PulseRifle,
          WeaponId.ScatterShot,
          WeaponId.RailSniper,
          WeaponId.ShardLauncher,
          WeaponId.NanoRepeater,
          WeaponId.ArcWelder,
          WeaponId.PhaseRepeater,
          WeaponId.PlasmaCannon,
        ];
        weaponId = weaponOptions[Math.floor(Math.random() * weaponOptions.length)];
      } else if (supplyType === SupplyType.BombCall || supplyType === SupplyType.MissileStrike) {
        amount = 1;
      }
    } else {
      // Original weighted random logic
      const roll = Math.random();
      
      if (roll < 0.30) { // 30% weapon
        supplyType = SupplyType.Weapon;
        const weaponOptions = [
          WeaponId.NeoSMG9,
          WeaponId.PulseRifle,
          WeaponId.ScatterShot,
          WeaponId.RailSniper,
          WeaponId.ShardLauncher,
          WeaponId.NanoRepeater,
          WeaponId.ArcWelder,
          WeaponId.PhaseRepeater,
          WeaponId.PlasmaCannon,
        ];
        weaponId = weaponOptions[Math.floor(Math.random() * weaponOptions.length)];
      } else if (roll < 0.48) { // 18% health kit
        supplyType = SupplyType.HealthKit;
        amount = 50; // Heal 50 HP
      } else if (roll < 0.63) { // 15% shield
        supplyType = SupplyType.Shield;
        amount = 50; // Add 50 shield
      } else if (roll < 0.78) { // 15% credits
        supplyType = SupplyType.Credits;
        // Generate credits in multiples of 100, weighted towards lower amounts
        const creditRoll = Math.random();
        if (creditRoll < 0.30) amount = 100;        // 30% chance
        else if (creditRoll < 0.50) amount = 200;   // 20% chance
        else if (creditRoll < 0.65) amount = 300;   // 15% chance
        else if (creditRoll < 0.77) amount = 400;   // 12% chance
        else if (creditRoll < 0.86) amount = 500;   // 9% chance
        else if (creditRoll < 0.92) amount = 800;   // 6% chance
        else if (creditRoll < 0.96) amount = 1000;  // 4% chance
        else if (creditRoll < 0.98) amount = 1500;  // 2% chance
        else if (creditRoll < 0.99) amount = 2000;  // 1% chance
        else amount = 2500;                         // 1% chance (rare)
      } else if (roll < 0.88) { // 10% grenade
        supplyType = SupplyType.Grenade;
        amount = 1;
      } else if (roll < 0.94) { // 6% bomb call radio
        supplyType = SupplyType.BombCall;
        amount = 1;
      } else { // 6% missile strike radio
        supplyType = SupplyType.MissileStrike;
        amount = 1;
      }
    }
    
    const crate = new SupplyCrate(dropPosition, supplyType, this.time, 200, weaponId, amount);
    this.supplyCrates.set(crate.id, crate);
    this.collisionWorld.addBody(crate.getCollisionBody());
    
    console.log(`[SupplyDrop] Crate dropped at (${dropPosition.x.toFixed(0)}, ${dropPosition.y.toFixed(0)}): ${supplyType}`);
  }

  private dropBomb(aircraft: Aircraft): void {
    // Drop bomb from back of aircraft
    const rotation = aircraft.getRotation();
    const dropOffset = -50; // Drop from behind the aircraft (back of B-52)
    
    const dropPosition = {
      x: aircraft.position.x + Math.cos(rotation) * dropOffset,
      y: aircraft.position.y + Math.sin(rotation) * dropOffset,
    };
    
    // Create bomb with reduced velocity (1/4 of aircraft speed)
    // This makes bombs fall behind the aircraft and be more visible
    // Altitude 1200 for 3 second fall time (1200 / 0.4 = 3000ms)
    const aircraftVelocity = aircraft.getVelocity();
    const bombVelocity = {
      x: aircraftVelocity.x * 0.25,
      y: aircraftVelocity.y * 0.25,
    };
    
    const bomb = new Bomb(
      aircraft.id,
      dropPosition,
      bombVelocity,
      1200 // High altitude for 3 second fall time with perspective
    );
    
    this.bombs.set(bomb.id, bomb);
    this.collisionWorld.addBody(bomb.getCollisionBody());
    
    console.log(`[Bomber] Bomb dropped at (${dropPosition.x.toFixed(0)}, ${dropPosition.y.toFixed(0)}) with altitude 1200`);
  }

  private handleBombExplosion(bomb: Bomb): void {
    const explosionRadius = 200; // Larger than grenades (150)
    const baseDamage = 120; // Higher than grenades (80)

    // Find all players in explosion radius
    for (const player of this.players.values()) {
      if (!player.alive) continue;

      const dist = Vec2.distance(player.position, bomb.position);
      if (dist <= explosionRadius) {
        // Damage falls off with distance
        const damageFalloff = 1 - (dist / explosionRadius);
        const damage = Math.floor(baseDamage * damageFalloff);

        if (damage > 0) {
          player.takeDamage(damage, bomb.ownerId, this.time);

          // Record damage event
          this.pendingEvents.push({
            type: GameEventType.PlayerDamage,
            data: {
              targetId: player.id,
              attackerId: bomb.ownerId,
              damage,
              position: bomb.position,
              isHeadshot: false,
            },
          });

          // Check for kill
          if (!player.alive) {
            const attacker = this.players.get(bomb.ownerId);
            if (attacker) {
              attacker.kills++;
              attacker.credits += GAMEPLAY.KILL_REWARD;
              attacker.ultimateCharge = Math.min(10, attacker.ultimateCharge + 2);
            }

            this.collisionWorld.removeBody(player.id);

            this.pendingEvents.push({
              type: GameEventType.PlayerKill,
              data: {
                victimId: player.id,
                killerId: bomb.ownerId,
                weaponId: 'bomb',
                isHeadshot: false,
              },
            });
          }
        }
      }
    }

    // Emit explosion event for visual/audio
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'bomb_explosion',
        position: bomb.position,
        radius: explosionRadius,
      },
    });

    console.log(`[Bomber] Bomb exploded at (${bomb.position.x.toFixed(0)}, ${bomb.position.y.toFixed(0)}) with radius ${explosionRadius}`);
  }

  private handleCratePickup(player: Player, crate: SupplyCrate): void {
    let pickupSound = 'item'; // Default sound type
    let pickupMessage = '';
    
    switch (crate.supplyType) {
      case SupplyType.Weapon:
        if (crate.weaponId) {
          const success = player.buyWeapon(crate.weaponId, true); // true = free pickup
          const weaponName = crate.weaponId.replace(/_/g, ' ').toUpperCase();
          pickupMessage = weaponName;
          console.log(`[SupplyDrop] ${player.name} picked up ${crate.weaponId} - Added to inventory: ${success}`);
          pickupSound = 'weapon';
        }
        break;
      
      case SupplyType.HealthKit:
        const healAmount = crate.amount || 50;
        player.health = Math.min(player.maxHealth, player.health + healAmount);
        pickupMessage = `+${healAmount} HP`;
        console.log(`[SupplyDrop] ${player.name} healed ${healAmount} HP`);
        pickupSound = 'health';
        break;
      
      case SupplyType.Shield:
        const shieldAmount = crate.amount || 50;
        player.shield = Math.min(player.maxShield, player.shield + shieldAmount);
        pickupMessage = `+${shieldAmount} SHIELD`;
        console.log(`[SupplyDrop] ${player.name} gained ${shieldAmount} shield`);
        pickupSound = 'item';
        break;
      
      case SupplyType.Credits:
        const creditAmount = crate.amount || 1000;
        player.credits += creditAmount;
        pickupMessage = `+$${creditAmount}`;
        console.log(`[SupplyDrop] ${player.name} gained $${creditAmount}`);
        pickupSound = 'item';
        break;
      
      case SupplyType.Grenade:
        const grenadeAmount = crate.amount || 1;
        const oldCount = player.grenadeCount;
        player.grenadeCount = Math.min(9, player.grenadeCount + grenadeAmount);
        pickupMessage = `+${grenadeAmount} GRENADE${grenadeAmount > 1 ? 'S' : ''}`;
        console.log(`[SupplyDrop] ${player.name} picked up ${grenadeAmount} grenade(s) - Before: ${oldCount}, After: ${player.grenadeCount}`);
        pickupSound = 'item';
        break;
      
      case SupplyType.BombCall:
        if (player.bombCallCount < 1) {
          player.bombCallCount = 1;
          pickupMessage = 'B-52 BOMBER CALL';
          console.log(`[SupplyDrop] ${player.name} picked up Bomb Call Radio`);
          pickupSound = 'weapon';
        } else {
          pickupMessage = 'RADIO FULL';
          console.log(`[SupplyDrop] ${player.name} already has Bomb Call Radio`);
          pickupSound = 'item';
        }
        break;
      
      case SupplyType.MissileStrike:
        if (player.missileStrikeCount < 1) {
          player.missileStrikeCount = 1;
          pickupMessage = 'MISSILE STRIKE CALL';
          console.log(`[SupplyDrop] ${player.name} picked up Missile Strike Radio`);
          pickupSound = 'weapon';
        } else {
          pickupMessage = 'RADIO FULL';
          console.log(`[SupplyDrop] ${player.name} already has Missile Strike Radio`);
          pickupSound = 'item';
        }
        break;
    }
    
    crate.claim();
    
    // Emit pickup event with sound type and message
    console.log(`[SupplyDrop] Emitting pickup event - sound type: ${pickupSound}, message: ${pickupMessage}`);
    this.pendingEvents.push({
      type: GameEventType.RoundStarted,
      data: {
        eventType: 'crate_pickup',
        playerId: player.id,
        pickupSound: pickupSound,
        pickupMessage: pickupMessage,
      },
    });
  }

  private checkRoundEnd(): void {
    // Skip round end checks if we don't have enough players yet
    const totalPlayers = this.players.size;
    if (totalPlayers < 2) {
      // Single player mode - don't end round
      return;
    }
    
    // Grace period: Don't check round end in the first 10 seconds
    // This gives plenty of time for network players to spawn and sync properly
    const roundTime = this.time - this.roundStartTime;
    if (roundTime < 10000) {
      return;
    }

    const attackerPlayers = this.getTeamPlayers(Team.Attackers);
    const defenderPlayers = this.getTeamPlayers(Team.Defenders);
    const attackersAlive = attackerPlayers.filter(p => p.alive).length;
    const defendersAlive = defenderPlayers.filter(p => p.alive).length;
    
    // Debug logging - log every 2 seconds to diagnose restart issue
    if (Math.floor(roundTime / 2000) !== Math.floor((roundTime - 16) / 2000)) {
      console.log(`[GameWorld] Round check @ ${Math.round(roundTime)}ms - Total: ${totalPlayers}, Attackers: ${attackersAlive}/${attackerPlayers.length}, Defenders: ${defendersAlive}/${defenderPlayers.length}`);
      
      // Additional debug: list all players
      for (const p of this.players.values()) {
        console.log(`  - ${p.name} (${p.peerId}): team=${p.team}, alive=${p.alive}`);
      }
    }
    
    // SAFETY: Don't end round if a team has no players at all
    // This can happen in multiplayer when waiting for players to sync
    if (attackerPlayers.length === 0 || defenderPlayers.length === 0) {
      // One team is empty - don't end round, wait for players to join
      return;
    }

    // For Deathmatch, check if one team is eliminated
    if (this.config.mode === GameMode.NeonDeathmatch) {
      if (attackersAlive === 0 && attackerPlayers.length > 0) {
        console.log(`[GameWorld] Round ending: All attackers eliminated`);
        this.endRound(Team.Defenders);
        return;
      }
      if (defendersAlive === 0 && defenderPlayers.length > 0) {
        console.log(`[GameWorld] Round ending: All defenders eliminated`);
        this.endRound(Team.Attackers);
        return;
      }
    } else {
      // Data Spike / Escort modes
      if (attackersAlive === 0 && attackerPlayers.length > 0) {
        console.log(`[GameWorld] Round ending: All attackers eliminated`);
        this.endRound(Team.Defenders);
        return;
      }
      if (defendersAlive === 0 && defenderPlayers.length > 0) {
        console.log(`[GameWorld] Round ending: All defenders eliminated`);
        this.endRound(Team.Attackers);
        return;
      }
    }

    // Check time limit
    if (roundTime >= GAMEPLAY.ROUND_TIME) {
      // Time out - defenders win
      this.endRound(Team.Defenders);
      return;
    }

    // Check spike upload (Data Spike mode)
    if (this.objectives.spikePlanted) {
      const uploadTime = this.time - this.objectives.spikePlantTime;
      this.objectives.spikeProgress = uploadTime / GAMEPLAY.SPIKE_UPLOAD_TIME;

      if (this.objectives.spikeProgress >= 1) {
        this.endRound(Team.Attackers);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Input Processing
  // -------------------------------------------------------------------------

  processInput(peerId: string, input: PlayerInput): void {
    const player = this.playersByPeerId.get(peerId);
    if (player) {
      player.setInput(input);
    }
  }

  // -------------------------------------------------------------------------
  // State Sync
  // -------------------------------------------------------------------------

  getState(): GameState {
    return {
      phase: this.phase,
      mode: this.config.mode,
      mapId: this.map.data.id,
      tick: this.tick,
      time: this.time,
      roundStartTime: this.roundStartTime,
      roundNumber: this.roundNumber,
      maxRounds: GAMEPLAY.MAX_ROUNDS,
      roundTimeLimit: GAMEPLAY.ROUND_TIME,
      attackerScore: this.attackerScore,
      defenderScore: this.defenderScore,
      // Use peerId as key for network sync (entity IDs differ between host and client)
      players: new Map(
        Array.from(this.players.values()).map(p => [p.peerId, p.toState()])
      ),
      projectiles: new Map(
        Array.from(this.projectiles.entries()).map(([id, p]) => [id, p.toState()])
      ),
      objectives: { ...this.objectives },
      // Include recent shots for client bullet tracer visualization
      recentShots: [...this.recentShots],
      // Countdown state for synchronized display
      countdownTime: this.countdownTime,
      countdownPaused: this.countdownPaused,
      countdownPausedBy: this.countdownPausedBy,
      // Supply drop system
      aircraft: new Map(
        Array.from(this.aircraft.entries()).map(([id, a]) => [id, a.toState()])
      ),
      supplyCrates: new Map(
        Array.from(this.supplyCrates.entries()).map(([id, c]) => [id, c.toState()])
      ),
      bombs: new Map(
        Array.from(this.bombs.entries()).map(([id, b]) => [id, b.toState()])
      ),
      grenades: new Map(
        Array.from(this.grenades.entries()).map(([id, g]) => [id, g.toState()])
      ),
    };
  }

  applyState(state: GameState): void {
    this.phase = state.phase;
    this.tick = state.tick;
    this.time = state.time;
    this.roundStartTime = state.roundStartTime;
    this.roundNumber = state.roundNumber;
    this.attackerScore = state.attackerScore;
    this.defenderScore = state.defenderScore;
    
    // Sync countdown state
    if (state.countdownTime !== undefined) {
      this.countdownTime = state.countdownTime;
    }
    if (state.countdownPaused !== undefined) {
      this.countdownPaused = state.countdownPaused;
    }
    if (state.countdownPausedBy !== undefined) {
      this.countdownPausedBy = state.countdownPausedBy;
    }
    this.objectives = { ...state.objectives };

    // Store local player's peerId before applying state
    const localPlayer = this.getLocalPlayer();
    const localPeerId = localPlayer?.peerId;

    // Update players (keyed by peerId for network sync)
    const seenPeerIds = new Set<string>();
    for (const [peerId, playerState] of state.players) {
      seenPeerIds.add(peerId);
      let player = this.playersByPeerId.get(peerId);
      if (!player) {
        // New player from host
        player = Player.fromState(playerState);
        this.players.set(player.id, player);
        this.playersByPeerId.set(peerId, player);
        this.collisionWorld.addBody(player.getCollisionBody());
        console.log(`[GameWorld] Created player from state: ${player.name} (${peerId})`);
        
        // If this is our local player (same peerId), update localPlayerId
        if (peerId === localPeerId) {
          this.localPlayerId = player.id;
          console.log(`[GameWorld] Updated localPlayerId to ${player.id} for peerId ${peerId}`);
        }
      } else {
        // Check if this is our local player - use client-side prediction
        const isLocalPlayer = player.id === this.localPlayerId;
        
        if (isLocalPlayer) {
          // For local player: only sync critical state, NOT position (client predicts movement)
          // This prevents jittery movement from host overwriting local prediction
          
          // EXCEPTION: Sync position on respawn/round start scenarios:
          // 1. Round number changed (new round started, everyone respawns)
          // 2. Player just respawned (was dead, now alive)
          // 3. Large distance mismatch (teleport or forced position sync)
          const wasRoundReset = state.roundNumber !== this.roundNumber;
          const justRespawned = !player.alive && playerState.alive;
          const dx = playerState.position.x - player.position.x;
          const dy = playerState.position.y - player.position.y;
          const distSq = dx * dx + dy * dy;
          const largeDistanceMismatch = distSq > 10000; // > 100 pixels difference
          
          if (wasRoundReset || justRespawned || largeDistanceMismatch) {
            // Force position sync from host
            player.position = { ...playerState.position };
            player.prevPosition = { ...playerState.position };
            player.velocity = { ...playerState.velocity };
            player.rotation = playerState.rotation;
            player.prevRotation = playerState.rotation;
            console.log(`[GameWorld] Force synced local player position (round: ${wasRoundReset}, respawn: ${justRespawned}, dist: ${Math.sqrt(distSq).toFixed(0)})`);
          }
          
          player.health = playerState.health;
          player.shield = playerState.shield;
          player.alive = playerState.alive;
          player.weapon = { ...playerState.weapon };
          player.abilities = playerState.abilities.map(a => ({ ...a }));
          player.team = playerState.team;
        } else {
          // For remote players: set target for smooth interpolation
          // Don't snap directly to position - interpolate smoothly
          player.targetPosition = { ...playerState.position };
          player.targetRotation = playerState.rotation;
          player.velocity = { ...playerState.velocity };
          player.health = playerState.health;
          player.shield = playerState.shield;
          player.alive = playerState.alive;
          player.weapon = { ...playerState.weapon };
          player.abilities = playerState.abilities.map(a => ({ ...a }));
          player.team = playerState.team;
        }
      }
    }

    // Remove players that are no longer in state (disconnected)
    for (const [id, player] of this.players) {
      if (!seenPeerIds.has(player.peerId)) {
        // Don't remove our own local player even if host doesn't have us yet
        if (id === this.localPlayerId) {
          console.log(`[GameWorld] Keeping local player even though not in host state: ${player.name}`);
          continue;
        }
        console.log(`[GameWorld] Removing player not in state: ${player.name} (${player.peerId})`);
        this.removePlayer(id);
      }
    }

    // Sync projectiles from host
    const seenProjectileIds = new Set<EntityId>();
    for (const [id, projState] of state.projectiles) {
      seenProjectileIds.add(id);
      let projectile = this.projectiles.get(id);
      if (!projectile) {
        // New projectile from host
        projectile = Projectile.fromState(projState);
        this.projectiles.set(id, projectile);
      } else {
        // Update existing projectile
        projectile.position = { ...projState.position };
        projectile.velocity = { ...projState.velocity };
      }
    }

    // Remove projectiles that are no longer in state
    for (const id of this.projectiles.keys()) {
      if (!seenProjectileIds.has(id)) {
        this.projectiles.delete(id);
      }
    }

    // Sync aircraft from host
    if (state.aircraft) {
      const seenAircraftIds = new Set<EntityId>();
      for (const [id, aircraftState] of state.aircraft) {
        seenAircraftIds.add(id);
        let aircraft = this.aircraft.get(id);
        if (!aircraft) {
          aircraft = Aircraft.fromState(aircraftState, this.time);
          this.aircraft.set(id, aircraft);
        } else {
          aircraft.position = { ...aircraftState.position };
          aircraft.active = aircraftState.active;
          aircraft.hasDropped = aircraftState.hasDropped;
        }
      }
      for (const id of this.aircraft.keys()) {
        if (!seenAircraftIds.has(id)) {
          this.aircraft.delete(id);
        }
      }
    }

    // Sync supply crates from host
    if (state.supplyCrates) {
      const seenCrateIds = new Set<EntityId>();
      for (const [id, crateState] of state.supplyCrates) {
        seenCrateIds.add(id);
        let crate = this.supplyCrates.get(id);
        if (!crate) {
          crate = SupplyCrate.fromState(crateState);
          this.supplyCrates.set(id, crate);
        } else {
          crate.position = { ...crateState.position };
          crate.altitude = crateState.altitude;
          crate.parachuteActive = crateState.parachuteActive;
          crate.claimed = crateState.claimed;
          crate.active = crateState.active;
        }
      }
      for (const id of this.supplyCrates.keys()) {
        if (!seenCrateIds.has(id)) {
          this.supplyCrates.delete(id);
          this.collisionWorld.removeBody(id);
        }
      }
    }

    // Sync bombs from host
    if (state.bombs) {
      const seenBombIds = new Set<EntityId>();
      for (const [id, bombState] of state.bombs) {
        seenBombIds.add(id);
        let bomb = this.bombs.get(id);
        if (!bomb) {
          bomb = Bomb.fromState(bombState);
          this.bombs.set(id, bomb);
          this.collisionWorld.addBody(bomb.getCollisionBody());
        } else {
          bomb.position = { ...bombState.position };
          bomb.velocity = { ...bombState.velocity };
          bomb.altitude = bombState.altitude;
          bomb.active = bombState.active;
          bomb.exploded = bombState.exploded;
        }
      }
      for (const id of this.bombs.keys()) {
        if (!seenBombIds.has(id)) {
          this.bombs.delete(id);
          this.collisionWorld.removeBody(id);
        }
      }
    }

    // Sync grenades from host
    if (state.grenades) {
      const seenGrenadeIds = new Set<EntityId>();
      for (const [id, grenadeState] of state.grenades) {
        seenGrenadeIds.add(id);
        let grenade = this.grenades.get(id);
        if (!grenade) {
          grenade = Grenade.fromState(grenadeState);
          this.grenades.set(id, grenade);
          this.collisionWorld.addBody(grenade.getCollisionBody());
        } else {
          grenade.position = { ...grenadeState.position };
          grenade.velocity = { ...grenadeState.velocity };
          grenade.rotation = grenadeState.rotation;
          grenade.fuseTime = grenadeState.fuseTime;
          grenade.active = grenadeState.active;
          grenade.exploded = grenadeState.exploded;
        }
      }
      for (const id of this.grenades.keys()) {
        if (!seenGrenadeIds.has(id)) {
          this.grenades.delete(id);
          this.collisionWorld.removeBody(id);
        }
      }
    }
  }

  getPendingEvents(): Array<{ type: GameEventType; data: unknown }> {
    const events = [...this.pendingEvents];
    this.pendingEvents = [];
    return events;
  }

  // -------------------------------------------------------------------------
  // Getters
  // -------------------------------------------------------------------------

  getPhase(): GamePhase {
    return this.phase;
  }

  getTick(): number {
    return this.tick;
  }

  getTime(): number {
    // Return time elapsed since round started
    return this.time - this.roundStartTime;
  }

  getAbsoluteTime(): number {
    // Return absolute game time (for death tracking, etc.)
    return this.time;
  }

  getRoundNumber(): number {
    return this.roundNumber;
  }

  getMap(): GameMap {
    return this.map;
  }

  getProjectiles(): Projectile[] {
    return Array.from(this.projectiles.values());
  }

  getGrenades(): Grenade[] {
    return Array.from(this.grenades.values());
  }

  getAircraft(): Aircraft[] {
    return Array.from(this.aircraft.values());
  }

  getSupplyCrates(): SupplyCrate[] {
    return Array.from(this.supplyCrates.values());
  }

  getBombs(): Bomb[] {
    return Array.from(this.bombs.values());
  }

  getRadioCallState(playerId: EntityId): { type: 'bomb' | 'missile' | null; firstClick: { x: number; y: number } | null; awaitingSecondClick: boolean } | null {
    return this.radioCallState.get(playerId) || null;
  }

  // Get recent shots for tracer visualization and clear the list
  getAndClearRecentShots(): Array<{ 
    start: { x: number; y: number }; 
    end: { x: number; y: number };
    shooterId: EntityId;
  }> {
    const shots = this.recentShots;
    this.recentShots = [];
    return shots;
  }

  // Check if a player is an enemy of the local player
  isEnemyOfLocal(playerId: EntityId): boolean {
    const localPlayer = this.getLocalPlayer();
    const player = this.getPlayer(playerId);
    if (!localPlayer || !player) return false;
    return localPlayer.team !== player.team;
  }

  getObjectives(): ObjectiveState {
    return { ...this.objectives };
  }

  getScore(): { attackers: number; defenders: number } {
    return { attackers: this.attackerScore, defenders: this.defenderScore };
  }

  getAttackerScore(): number {
    return this.attackerScore;
  }

  getDefenderScore(): number {
    return this.defenderScore;
  }

  getRoundsToWin(): number {
    return this.roundsToWin;
  }

  getTotalRounds(): number {
    return this.totalRounds;
  }

  getCollisionWorld(): CollisionWorld {
    return this.collisionWorld;
  }

  setPhase(phase: GamePhase): void {
    this.phase = phase;
  }

  // Countdown getters
  getCountdownTime(): number {
    return this.countdownTime;
  }

  isCountdownPaused(): boolean {
    return this.countdownPaused;
  }

  getCountdownPausedBy(): string {
    return this.countdownPausedBy;
  }
}
